// src/utils/format.js
//
// Formatteurs d'affichage CENTRALISÉS (locale fr-FR, franc Pacifique « F »).
// But : une seule source pour les formats répétés dans les écrans, au lieu de
// redéclarer `Intl.NumberFormat` / `fF` / `fmtMoney` / `fmtInt` / … dans chaque
// fichier (motif présent dans ~17 écrans).
//
// ⚠️ IMPORTANT : ces fonctions reproduisent EXACTEMENT le rendu des
// implémentations historiques qu'elles remplacent (mêmes arrondis, même
// séparateur de milliers, mêmes suffixes). Ne pas modifier un format sans
// vérifier tous ses appelants — l'affichage doit rester inchangé.

// Instance réutilisée (créer un Intl.NumberFormat est coûteux ; on le fait une fois).
const NF_FR = new Intl.NumberFormat("fr-FR");

/**
 * Arrondi entier robuste : `null`/`undefined`/`NaN` → 0.
 * @param {*} n
 * @returns {number}
 */
export const roundInt = (n) => Math.round(Number(n) || 0);

/**
 * Entier avec séparateur de milliers. Ex. `12345` → `"12 345"`.
 * @param {*} n
 * @returns {string}
 */
export const fmtQty = (n) => NF_FR.format(roundInt(n));

/**
 * Montant en francs Pacifique (entier, suffixe « F »). Ex. `12345` → `"12 345 F"`.
 * @param {*} n
 * @returns {string}
 */
export const fmtFranc = (n) => `${fmtQty(n)} F`;

// Instances par nombre de décimales (créer un Intl.NumberFormat est coûteux,
// et les tableaux Analyse CA formatent des dizaines de milliers de cellules).
const NF_FR_PAR_DEC = new Map();

/**
 * Nombre fr-FR à `dec` décimales maxi, milliers séparés par une espace
 * insécable NORMALE (U+00A0) : Intl fr-FR émet une espace FINE (U+202F), trop
 * étroite pour rester lisible sur les gros montants XPF (demande client du
 * 29/10/2026 sur les tableaux Analyse CA). Non-nombre → `"—"`.
 * @param {*} n
 * @param {number} [dec=0]
 * @returns {string}
 */
export const fmtNombreLarge = (n, dec = 0) => {
  if (typeof n !== "number" || !Number.isFinite(n)) return "—";
  let nf = NF_FR_PAR_DEC.get(dec);
  if (!nf) {
    nf = new Intl.NumberFormat("fr-FR", { maximumFractionDigits: dec });
    NF_FR_PAR_DEC.set(dec, nf);
  }
  // U+202F (espace fine, separateur Intl fr-FR) -> U+00A0 (espace normale),
  // les deux insecables. Codepoints construits en ASCII pur : un caractere
  // invisible en dur dans le source ne survit pas aux copier-coller.
  const FINE = String.fromCharCode(0x202f);
  const NORMALE = String.fromCharCode(0x00a0);
  return nf.format(n).split(FINE).join(NORMALE);
};

/**
 * Montant XPF : toujours ENTIER, milliers séparés par une espace large.
 * Le suffixe (« XPF ») reste à la charge de l'appelant.
 * @param {*} n
 * @returns {string}
 */
export const fmtMontant = (n) => fmtNombreLarge(n, 0);

/**
 * Pourcentage à N décimales (1 par défaut). `null`/`undefined` → `"—"`.
 * Ex. `fmtPct(33.333)` → `"33.3 %"`.
 * @param {*} n
 * @param {number} [decimals=1]
 * @returns {string}
 */
export const fmtPct = (n, decimals = 1) =>
  n === null || n === undefined ? "—" : `${Number(n).toFixed(decimals)} %`;

/**
 * Coefficient multiplicateur (PV / PR), 2 décimales, virgule française.
 * `null`/`undefined` → `"—"`. Ex. `fmtCoef(1.6666)` → `"1,67"`.
 * @param {*} n
 * @returns {string}
 */
export const fmtCoef = (n) =>
  n === null || n === undefined || !Number.isFinite(Number(n))
    ? "—"
    : Number(n).toLocaleString("fr-FR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

/**
 * Date courte fr-FR. Ex. `"05/08/2026"`. Entrée vide/invalide → `"—"`.
 * Accepte une `Date`, une chaîne ISO ou un timestamp.
 * @param {Date|string|number} value
 * @returns {string}
 */
export const fmtDate = (value) => {
  if (!value) return "—";
  const d = value instanceof Date ? value : new Date(value);
  return isNaN(d.getTime()) ? "—" : d.toLocaleDateString("fr-FR");
};
